/**
 * Read-only display resolution for casino curation entity names
 * (collections, tags, pages) using the TCM translation maps the admin API
 * attaches as `name_translations` / `description_translations` (CASENG-1053).
 *
 * Display only: edit-form prefill, rename payloads and anything sent back to
 * the API keep the canonical `name`.
 */
import i18n from "../../i18n";
import { canonicalizeLanguageTag } from "./supportedLanguages";

export type TranslationMap = Record<string, string>;

export type CurationNamedEntity = {
  name: string;
  name_translations?: TranslationMap | null;
};

export type CurationDescribedEntity = {
  description?: string | null;
  description_translations?: TranslationMap | null;
};

/**
 * Resolved at call time, never at module load: the i18n language is applied
 * after modules evaluate (see #2014), so a cached value would be stale.
 */
export function getActiveCurationLocale(): string | undefined {
  return i18n.resolvedLanguage || i18n.language || undefined;
}

/**
 * Picks the translated text for `locale` from a BE map, falling back to
 * `canonical`. Empty-string values fall back too (`||`, not `??`) — an empty
 * cached entry is "nothing to show", not a blank label.
 *
 * BE keys come from `available_content_locales()` and may be lower-cased
 * (`pt-br`) while the FE uses `pt-BR`, so the normalized key is tried first,
 * then the raw locale.
 */
export function resolveCurationText(
  translations: TranslationMap | null | undefined,
  canonical: string,
  locale: string | undefined = getActiveCurationLocale(),
): string {
  if (!translations || !locale) return canonical;
  const normalized = canonicalizeLanguageTag(locale);
  return (
    (normalized ? translations[normalized] : undefined) ||
    translations[locale] ||
    translations[locale.toLowerCase()] ||
    canonical
  );
}

export function getLocalizedCurationName(
  entity: CurationNamedEntity,
  locale: string | undefined = getActiveCurationLocale(),
): string {
  return resolveCurationText(entity.name_translations, entity.name, locale);
}

export function getLocalizedCurationDescription(
  entity: CurationDescribedEntity,
  locale: string | undefined = getActiveCurationLocale(),
): string {
  return resolveCurationText(
    entity.description_translations,
    entity.description ?? "",
    locale,
  );
}
